/**
 * Shared TypeScript contracts for deSjeff (Phase 2, built on the Phase-1 shell).
 *
 * Cross-cutting only (roles, principal, DTOs). The isolation model is
 * per-cookbook membership (NOT org-tenant); see db/ + the API auth layer.
 */
/**
 * Per-cookbook roles. A user holds one role per cookbook (in `cookbook_members`):
 *   owner   — full control (manage members, delete the cookbook)
 *   write   — create/edit/delete recipes
 *   comment — read + comment (no recipe edits)
 *   read    — cook-mode / read only
 * Authorization is enforced per request against the caller's role on the target
 * cookbook (deny-by-default; ARCHITECTURE §7), and as Postgres RLS as a backstop.
 */
export const COOKBOOK_ROLES = ["owner", "write", "comment", "read"];
/**
 * Which roles may create / edit / delete recipes (the `recipe:write` capability).
 * The API + Postgres RLS remain the AUTHORITATIVE enforcement; the SPA imports
 * this only to mirror affordances (e.g. show/hide an "Edit" button). Keeping it
 * here means the client hint and the contract share one definition.
 */
export function roleCanEditRecipes(role) {
    return role === "owner" || role === "write";
}
/**
 * How many recipes a FREE cookbook may hold (§1). Above this, a new recipe is
 * refused with 402 (`/problems/entitlement-required`); §2.2's SOFT LIMIT means
 * everything already stored stays fully readable/editable/deletable.
 *
 * MUST MIRROR `free_plan_recipe_limit()` in
 * `db/migrations/0020_free_plan_recipe_limit.sql`, which is the AUTHORITATIVE
 * copy (RLS is the last line — it catches every write path that forgets the
 * app-layer check). The two are not redundant, they are the two layers §5
 * demands: the app produces the friendly 402, RLS produces a blunt 42501.
 *
 * If they ever diverge, ONE direction is safe:
 *   - app STRICTER than RLS (this constant LOWER than the SQL one) ⇒ the user
 *     always gets the pretty 402. Safe, just conservative.
 *   - RLS STRICTER than the app (this constant HIGHER than the SQL one) ⇒ the
 *     app waves the write through and Postgres rejects it, surfacing as an
 *     ugly 500. Never ship that direction.
 * Raising the tier therefore means: change the SQL function FIRST (a new
 * migration), then this constant.
 */
export const FREE_PLAN_RECIPE_LIMIT = 15;
/**
 * The fixed, curated set of recipe categories the editor's picker offers
 * (`apps/web/src/pages/RecipeEditorPage.tsx`'s single-select) — the ONE
 * source of truth both the SPA and the API import layer read from. Order
 * matters (it's the picker's display order); "Saus" was added after
 * "Småretter" (the owner's request — reads naturally as a savory/side
 * category, right after where sauces-as-side-dishes conceptually sit,
 * before the sweet categories start).
 *
 * NOTE: `recipes.category` itself remains FREE TEXT in the DB/DTOs — a user
 * can still type any string via a direct API call (out of scope to lock
 * down here, see `normalizeRecipeCategory`'s docs). This list is what the
 * editor's picker offers AND what the import layer is restricted to (see
 * `normalizeRecipeCategory`) so imported recipes never invent categories
 * like "Hovedrett • Fisk" that then pollute the feed's category filter.
 */
export const RECIPE_CATEGORIES = [
    "Frokost",
    "Lunsj",
    "Middag",
    "Småretter",
    "Saus",
    "Dessert",
    "Kaker",
    "Gjærbakst",
];
/**
 * The single place the "is this a real (preset) category?" question is
 * answered. Trims `value`, matches it case-insensitively against
 * `RECIPE_CATEGORIES`, and returns the CANONICAL preset spelling on a match
 * (e.g. `"middag"` → `"Middag"`) — never the caller's original casing.
 * Returns `null` for anything that doesn't match a preset exactly
 * (including a comma/bullet-joined invented value like "Hovedrett, Matbit,
 * Svinekjøtt"), or for `null`/`undefined`/empty/whitespace-only input.
 *
 * Used by the recipe-import mapping (`mapExtractionToDraft`) to guarantee an
 * import NEVER stores an invented category — better an empty category than
 * a guessed one (the owner's explicit call). Deliberately NOT used to
 * validate the normal create/update DTO — a user typing a custom category
 * via the API is out of scope; the editor's picker is what governs there.
 */
export function normalizeRecipeCategory(value) {
    if (typeof value !== "string") {
        return null;
    }
    const trimmed = value.trim();
    if (trimmed.length === 0) {
        return null;
    }
    const lower = trimmed.toLowerCase();
    return RECIPE_CATEGORIES.find((preset) => preset.toLowerCase() === lower) ?? null;
}
/**
 * Split a free-text ingredient amount (e.g. "2 dl", "400 g", "håndfull") into a
 * scalable `quantity` + free-text `unit`, matching `IngredientItem`. This is the
 * SINGLE source of truth for that rule: the URL/photo import mapper and the seed
 * scripts call it, and db/migrations/0014 mirrors it in SQL for the one-time
 * backfill of pre-split recipes — keep the three in sync.
 *
 * Rule (data-preserving, never drops text):
 *  - a LEADING plain number (integer or decimal, `.` or `,`) that is followed by
 *    whitespace or end-of-string → `quantity` = that number, `unit` = the rest
 *    ("2 dl" → 2 + "dl"; "400 g" → 400 + "g"; "2" → 2 + "").
 *  - anything else — no leading number, a range ("1-2"), a unicode fraction
 *    ("½"), "ca 2 dl", "håndfull" → `quantity` = null and the WHOLE trimmed
 *    input is kept verbatim in `unit` (renders unchanged, just not scalable).
 */
export function parseIngredientAmount(amount) {
    const text = amount ?? "";
    const m = /^\s*(\d+(?:[.,]\d+)?)(?:\s|$)/.exec(text);
    if (!m) {
        return { quantity: null, unit: text.trim() };
    }
    const quantity = Number((m[1] ?? "").replace(",", "."));
    const unit = text.replace(/^\s*\d+(?:[.,]\d+)?\s*/, "").trim();
    return { quantity, unit };
}
/**
 * Format an ingredient row's amount portion for display: `quantity` (rendered
 * with a Norwegian comma decimal, trailing zeros dropped) followed by `unit`,
 * either of which may be empty. E.g. {3, "dl"} → "3 dl"; {2.5, "dl"} → "2,5 dl";
 * {null, "håndfull"} → "håndfull"; {2, ""} → "2". Used by the read view, cook
 * mode (on the scaled quantity) and the AI search/embedding text.
 */
export function formatIngredientAmount(item) {
    // `== null` catches both null and a defensively-omitted undefined (a
    // hand-crafted API/AI client could omit the key) — never render "NaN".
    const qty = item.quantity == null ? "" : String(Math.round(item.quantity * 100) / 100).replace(".", ",");
    return [qty, item.unit].filter((part) => part.length > 0).join(" ");
}
// ---------------------------------------------------------------------------
// Cookbook sharing & invites (P2-F2, db/migrations/0010_cookbook_invites.sql).
// A cookbook OWNER invites people by email with a role of `write` or `read`
// (never `owner`/`comment`) — existing + new users are handled UNIFORMLY via
// a pending invite that resolves on the invitee's next `/me` call (see
// `MeService`'s resolve-on-login step): an existing user is auto-added with
// no separate "accept" step, a brand-new email registers + passes the
// closed-pilot approval gate first. All management endpoints
// (`/cookbooks/:cookbookId/{invites,access,members}`) are OWNER-only
// (`cookbook:manage`), same PEP as every other cookbook-scoped route.
// ---------------------------------------------------------------------------
/**
 * The maximum number of members (owner + invited) a single cookbook may ever
 * have — owner + accepted members + still-pending invites all count toward
 * this cap (`CookbookAccessService.invite`). Change this ONE constant to
 * raise the cap; nothing else in the codebase hardcodes the number 5.
 */
export const MAX_COOKBOOK_MEMBERS = 5;
//# sourceMappingURL=index.js.map